import { Button, HStack } from '@montel/montelpro-shared-components-v2';
import { AccessControl } from 'components/common/AccessControl/AccessControl';
import { AdminRole } from 'domain/account/enums';
import { SubscriptionBundle } from 'domain/subscription/interfaces/SubscriptionBundle';
import { SubscriptionStatus } from 'domain/subscription/interfaces/Subscription';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import useAuthRoles from 'hooks/useAuthRoles';
import { CancelBundleModal } from './CancelBundleModal/CancelBundleModal';
import { DeleteBundleModal } from './DeleteBundleModal/DeleteBundleModal';
import { ConditionallyDisabled } from 'components/common/ConditionallyDisabled/ConditionallyDisabled';

export const BundleActions: React.FC<{
	bundle: SubscriptionBundle;
	onBundleUpdated: () => void;
}> = ({ bundle, onBundleUpdated }) => {
	const [showCancelModal, setShowCancelModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const navigate = useNavigate();
	const { adminIsRestrictedToRole: isRestrictedToRole } = useAuthRoles();

	const canBeCancelled =
		bundle?.status === SubscriptionStatus.Active ||
		bundle?.status === SubscriptionStatus.Ordered;

	const canBeDeleted = (bundle?.subscriptions?.length ?? 0) === 0;

	const onBundleCancelled = () => {
		onBundleUpdated();
	};

	const onBundleDeleted = () => {
		navigate(-1);
	};

	return (
		<>
			<HStack gap="md" align="center">
				<Button
					variant="secondary"
					text="Edit Bundle"
					size="md"
					isDisabled={isRestrictedToRole(AdminRole.SALES)}
					onClick={() =>
						navigate(`/subscription-bundles/${bundle.id}/edit`)
					}
				/>
				<AccessControl
					allowedRoles={[
						AdminRole.SUPER_ADMIN,
						AdminRole.MONTEL_ACCOUNT_ADMIN,
					]}
				>
					<Button
						variant="negative"
						text="Cancel Bundle"
						size="md"
						isDisabled={!canBeCancelled}
						onClick={() => setShowCancelModal(true)}
					/>
				</AccessControl>
				<AccessControl allowedRoles={[AdminRole.SUPER_ADMIN]}>
					<ConditionallyDisabled
						disableIf={!canBeDeleted}
						tooltip="A bundle can only be deleted if it contains no subscriptions."
					>
						<Button
							variant="negative"
							text="Delete Bundle"
							size="md"
							onClick={() => setShowDeleteModal(true)}
						/>
					</ConditionallyDisabled>
				</AccessControl>
			</HStack>

			<CancelBundleModal
				showModal={showCancelModal}
				setShowModal={setShowCancelModal}
				bundleId={bundle.id}
				onBundleCancelled={onBundleCancelled}
			/>
			<DeleteBundleModal
				showModal={showDeleteModal}
				setShowModal={setShowDeleteModal}
				bundleId={bundle.id}
				onBundleDeleted={onBundleDeleted}
			/>
		</>
	);
};
